/* ==========================================================================
   ZORA — Collection runtime
   Filtering, sorting, quick chips, grid density, load more / infinite scroll.
   Everything updates the URL, so filtered views stay shareable and the back
   button behaves.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-z-collection]') || document.querySelector('[data-z-search-page]');
  if (!root) return;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function $(s, r) {
    return (r || document).querySelector(s);
  }
  function $$(s, r) {
    return Array.prototype.slice.call((r || document).querySelectorAll(s));
  }
  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  var results = $('[data-z-results]', root);
  var panel = $('[data-z-facets-panel]', root);

  /* ======================================================================
     Fetch + swap
     ====================================================================== */
  var inFlight = null;

  function setBusy(state) {
    if (!results) return;
    results.setAttribute('aria-busy', state ? 'true' : 'false');
    results.classList.toggle('is-loading', state);
  }

  function swapFrom(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');

    // Results grid + pagination
    var freshResults = doc.querySelector('[data-z-results]');
    if (freshResults && results) results.innerHTML = freshResults.innerHTML;

    // Facets — counts and disabled states change with every selection
    var freshPanel = doc.querySelector('[data-z-facets-panel]');
    if (freshPanel && panel) {
      var wasOpen = panel.classList.contains('is-open');
      var openGroups = $$('[data-z-facet]', panel)
        .filter(function (d) {
          return d.open;
        })
        .map(function (d) {
          return d.getAttribute('data-param');
        });

      panel.innerHTML = freshPanel.innerHTML;

      // Preserve which groups the shopper had expanded
      $$('[data-z-facet]', panel).forEach(function (d) {
        if (openGroups.indexOf(d.getAttribute('data-param')) !== -1) d.open = true;
      });
      if (wasOpen) panel.classList.add('is-open');
      bindFacets();
    }

    // Quick filter chips
    var freshQuick = doc.querySelector('[data-z-quickfilter]');
    var quick = $('[data-z-quickfilter]', root);
    if (freshQuick && quick) quick.innerHTML = freshQuick.innerHTML;

    // Result count
    var freshCount = doc.querySelector('[data-z-result-count]');
    var count = $('[data-z-result-count]', root);
    if (freshCount && count) count.textContent = freshCount.textContent;

    if (window.Zora && window.Zora.initAll) window.Zora.initAll(root);
    bindPagination();
    bindQuickChips();
    document.dispatchEvent(new CustomEvent('zora:results:swapped'));
  }

  function load(url, pushState) {
    if (inFlight) inFlight.abort();
    inFlight = typeof AbortController !== 'undefined' ? new AbortController() : null;

    setBusy(true);

    return fetch(url, {
      signal: inFlight ? inFlight.signal : undefined,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Request failed');
        return r.text();
      })
      .then(function (html) {
        swapFrom(html);
        if (pushState !== false) history.pushState({ zora: true, url: url }, '', url);

        // Scroll the grid back into view, but only if it has scrolled off
        var top = results ? results.getBoundingClientRect().top : 0;
        if (top < -80) {
          var offset = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue('--z-header-h'),
            10
          ) || 0;
          window.scrollTo({
            top: window.pageYOffset + top - offset - 24,
            behavior: reduceMotion ? 'auto' : 'smooth',
          });
        }
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        // Fall back to a normal navigation so the shopper is never stuck
        window.location.href = url;
      })
      .finally(function () {
        setBusy(false);
      });
  }

  window.addEventListener('popstate', function (e) {
    if (e.state && e.state.zora) load(window.location.href, false);
    else window.location.reload();
  });

  /* ======================================================================
     Facet form
     ====================================================================== */
  function facetUrl(form) {
    var data = new FormData(form);
    var params = new URLSearchParams();

    data.forEach(function (value, key) {
      if (value === '' || value == null) return;
      params.append(key, value);
    });

    var base = form.getAttribute('action') || window.location.pathname;
    var qs = params.toString();
    return qs ? base + '?' + qs : base;
  }

  var submitFacets = debounce(function (form) {
    load(facetUrl(form));
  }, 380);

  function bindFacets() {
    var form = $('[data-z-facets]', root);
    if (!form || form.hasAttribute('data-bound')) {
      if (form) bindFacetExtras(form);
      return;
    }
    form.setAttribute('data-bound', '');

    form.addEventListener('change', function (e) {
      var t = e.target;
      if (t.name === 'sort_by' || t.type === 'checkbox') {
        submitFacets(form);
      } else if (t.type === 'number') {
        submitFacets(form);
      }
    });

    form.addEventListener('input', function (e) {
      if (e.target.type === 'number') submitFacets(form);
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      load(facetUrl(form));
    });

    bindFacetExtras(form);
  }

  function bindFacetExtras(form) {
    // Type-to-filter inside a long facet list
    $$('[data-z-facet-search]', form).forEach(function (input) {
      if (input.hasAttribute('data-bound')) return;
      input.setAttribute('data-bound', '');
      input.addEventListener('input', function () {
        var q = input.value.trim().toLowerCase();
        var list = input.closest('.z-facet__panel').querySelector('[data-z-facet-list]');
        if (!list) return;
        $$('li', list).forEach(function (li) {
          var label = li.getAttribute('data-z-facet-value') || '';
          li.hidden = q !== '' && label.indexOf(q) === -1;
        });
      });
    });

    // Show-all toggle on long lists
    $$('[data-z-facet-more]', form).forEach(function (btn) {
      if (btn.hasAttribute('data-bound')) return;
      btn.setAttribute('data-bound', '');
      var list = btn.previousElementSibling;
      if (list) list.classList.add('is-truncated');
      btn.addEventListener('click', function () {
        var open = list.classList.toggle('is-truncated') === false;
        btn.classList.toggle('is-open', open);
        var span = btn.querySelector('span');
        if (span) span.textContent = open ? 'Show less' : span.textContent;
      });
    });
  }

  // Chips, clear-all and quick facet links are plain <a> — intercept them
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('[data-z-facet-link]');
    if (!link || !root.contains(link)) return;
    if (link.getAttribute('aria-disabled') === 'true') {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    load(link.href);
  });

  /* ======================================================================
     Sort (toolbar)
     ====================================================================== */
  var sortSelect = $('[data-z-sort]', root);
  if (sortSelect) {
    sortSelect.addEventListener('change', function () {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      url.searchParams.delete('page');
      load(url.toString());
    });
  }

  /* ======================================================================
     Filter drawer
     ====================================================================== */
  (function drawer() {
    var openBtn = $('[data-z-facets-open]', root);
    if (!panel) return;

    function open() {
      panel.removeAttribute('hidden');
      requestAnimationFrame(function () {
        panel.classList.add('is-open');
      });
      document.body.classList.add('z-locked');
      if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
      var close = $('[data-z-facets-close]', panel);
      if (close) setTimeout(function () { close.focus(); }, 200);
    }

    function close() {
      panel.classList.remove('is-open');
      document.body.classList.remove('z-locked');
      if (openBtn) {
        openBtn.setAttribute('aria-expanded', 'false');
        openBtn.focus();
      }
      // Only the drawer layout hides itself; the sidebar stays put
      if (panel.closest('.z-collection__layout--drawer')) {
        setTimeout(function () {
          panel.setAttribute('hidden', '');
        }, 380);
      }
    }

    if (openBtn) {
      openBtn.addEventListener('click', function () {
        panel.classList.contains('is-open') ? close() : open();
      });
    }

    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-z-facets-close]');
      if (btn && panel.contains(btn)) close();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && panel.classList.contains('is-open')) close();
    });
  })();

  /* ======================================================================
     Grid / list view
     ====================================================================== */
  (function viewToggle() {
    var buttons = $$('[data-z-view]', root);
    if (!buttons.length) return;

    function apply(mode, persist) {
      var grid = $('[data-z-grid]', root);
      if (grid) grid.classList.toggle('is-list', mode === 'list');

      buttons.forEach(function (b) {
        var on = b.getAttribute('data-z-view') === mode;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      if (persist) {
        try {
          localStorage.setItem('zora:view', mode);
        } catch (e) {}
      }
    }

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        apply(btn.getAttribute('data-z-view'), true);
      });
    });

    // Restore the shopper's last choice
    var saved;
    try {
      saved = localStorage.getItem('zora:view');
    } catch (e) {}
    if (saved) apply(saved, false);

    // Re-apply after a filter swap replaces the grid
    document.addEventListener('zora:results:swapped', function () {
      var mode = 'grid';
      try {
        mode = localStorage.getItem('zora:view') || 'grid';
      } catch (e) {}
      apply(mode, false);
    });
  })();

  /* ======================================================================
     Client-side quick chips (used when no storefront facet exists)
     ====================================================================== */
  function bindQuickChips() {
    var wrap = $('[data-z-quickfilter]', root);
    if (!wrap) return;

    var chips = $$('[data-z-quickchip]', wrap);
    var clearBtn = $('[data-z-quickchip-clear]', wrap);
    if (!chips.length) return;

    var selected = [];

    function apply() {
      var items = $$('[data-z-grid-item]', root);
      var visible = 0;

      items.forEach(function (li) {
        if (!selected.length) {
          li.hidden = false;
          visible++;
          return;
        }
        var card = li.querySelector('[data-z-card]');
        var available = ((card && card.getAttribute('data-sizes-available')) || '')
          .split('|')
          .filter(Boolean);
        var match = selected.some(function (s) {
          return available.indexOf(s) !== -1;
        });
        li.hidden = !match;
        if (match) visible++;
      });

      var count = $('[data-z-result-count]', root);
      if (count) {
        count.textContent = visible + (visible === 1 ? ' product' : ' products');
      }
      if (clearBtn) clearBtn.hidden = selected.length === 0;
      wrap.classList.toggle('has-selection', selected.length > 0);
    }

    chips.forEach(function (chip) {
      if (chip.hasAttribute('data-bound')) return;
      chip.setAttribute('data-bound', '');
      chip.addEventListener('click', function () {
        var value = chip.getAttribute('data-value');
        var i = selected.indexOf(value);
        if (i === -1) selected.push(value);
        else selected.splice(i, 1);
        chip.classList.toggle('is-active', i === -1);
        chip.setAttribute('aria-pressed', i === -1 ? 'true' : 'false');
        apply();
      });
    });

    if (clearBtn && !clearBtn.hasAttribute('data-bound')) {
      clearBtn.setAttribute('data-bound', '');
      clearBtn.addEventListener('click', function () {
        selected = [];
        chips.forEach(function (c) {
          c.classList.remove('is-active');
          c.setAttribute('aria-pressed', 'false');
        });
        apply();
      });
    }
  }

  /* ======================================================================
     Load more / infinite scroll
     ====================================================================== */
  var appending = false;

  function appendNext(url, button) {
    if (appending) return;
    appending = true;

    if (button) {
      button.disabled = true;
      var label = button.querySelector('[data-z-load-more-label]');
      if (label) {
        button.setAttribute('data-label', label.textContent);
        label.textContent = 'Loading…';
      }
    }

    fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var grid = $('[data-z-grid]', root);
        var freshItems = doc.querySelectorAll('[data-z-grid-item]');

        if (grid && freshItems.length) {
          Array.prototype.forEach.call(freshItems, function (li) {
            grid.appendChild(li);
          });
        }

        // Replace the pagination block with the next page's version
        var oldPag = $('[data-z-pagination]', root);
        var newPag = doc.querySelector('[data-z-pagination]');
        if (oldPag) {
          if (newPag) oldPag.replaceWith(newPag);
          else oldPag.remove();
        }

        history.replaceState({ zora: true, url: url }, '', url);

        if (window.Zora && window.Zora.initAll) window.Zora.initAll(root);
        bindPagination();
      })
      .catch(function () {
        if (button) {
          button.disabled = false;
          var l = button.querySelector('[data-z-load-more-label]');
          if (l) l.textContent = button.getAttribute('data-label') || 'Load more';
        }
      })
      .finally(function () {
        appending = false;
      });
  }

  var infiniteObserver = null;

  function bindPagination() {
    var moreBtn = $('[data-z-load-more]', root);
    if (moreBtn && !moreBtn.hasAttribute('data-bound')) {
      moreBtn.setAttribute('data-bound', '');
      moreBtn.addEventListener('click', function () {
        appendNext(moreBtn.getAttribute('data-url'), moreBtn);
      });
    }

    var sentinel = $('[data-z-infinite]', root);
    if (sentinel && 'IntersectionObserver' in window) {
      if (infiniteObserver) infiniteObserver.disconnect();
      infiniteObserver = new IntersectionObserver(
        function (entries) {
          if (!entries[0].isIntersecting) return;
          infiniteObserver.disconnect();
          appendNext(sentinel.getAttribute('data-url'), null);
        },
        { rootMargin: '400px 0px' }
      );
      infiniteObserver.observe(sentinel);
    }
  }

  /* ======================================================================
     Boot
     ====================================================================== */
  bindFacets();
  bindPagination();
  bindQuickChips();
})();
